import React from 'react';
import { motion } from 'framer-motion';
import { FaUser, FaTruck } from 'react-icons/fa';

interface ClientInfoTabProps {
  clientName: string;
  clientAddress: string;
  clientPhone: string;
  clientEmail: string;
  shippingName: string;
  shippingAddress: string;
  shippingPhone: string;
  shippingEmail: string;
  onChange: (field: string, value: string) => void;
  t?: Record<string, string>;
}

const ClientInfoTab: React.FC<ClientInfoTabProps> = ({
  clientName,
  clientAddress,
  clientPhone,
  clientEmail,
  shippingName,
  shippingAddress,
  shippingPhone,
  shippingEmail,
  onChange,
  t
}) => {
  const copyClientToShipping = () => {
    onChange('shippingName', clientName);
    onChange('shippingAddress', clientAddress);
    onChange('shippingPhone', clientPhone);
    onChange('shippingEmail', clientEmail);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* معلومات العميل */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <FaUser className="text-blue-600" />
          {t?.clientInfo || 'معلومات العميل'}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.clientName || 'اسم العميل'}
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => onChange('clientName', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.clientNamePlaceholder || 'أدخل اسم العميل'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.clientEmail || 'البريد الإلكتروني'}
            </label>
            <input
              type="email"
              value={clientEmail}
              onChange={(e) => onChange('clientEmail', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.clientEmailPlaceholder || 'client@example.com'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.clientPhone || 'رقم الهاتف'}
            </label>
            <input
              type="tel"
              value={clientPhone}
              onChange={(e) => onChange('clientPhone', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.clientPhonePlaceholder || '+966 50 000 0000'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.clientAddress || 'عنوان العميل'}
            </label>
            <textarea
              value={clientAddress}
              onChange={(e) => onChange('clientAddress', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.clientAddressPlaceholder || 'أدخل عنوان العميل الكامل'}
            />
          </div>
        </div>
      </div>

      {/* معلومات الشحن */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <FaTruck className="text-blue-600" />
            {t?.shippingInfo || 'معلومات الشحن'}
          </h3>
          <button
            onClick={copyClientToShipping}
            className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/30 transition-colors"
          >
            {t?.copyFromClient || 'نسخ من العميل'}
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.shippingName || 'اسم المستلم'}
            </label>
            <input
              type="text"
              value={shippingName}
              onChange={(e) => onChange('shippingName', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.shippingNamePlaceholder || 'أدخل اسم المستلم'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.shippingEmail || 'البريد الإلكتروني'}
            </label>
            <input
              type="email"
              value={shippingEmail}
              onChange={(e) => onChange('shippingEmail', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.shippingEmailPlaceholder || 'shipping@example.com'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.shippingPhone || 'رقم الهاتف'}
            </label>
            <input
              type="tel"
              value={shippingPhone}
              onChange={(e) => onChange('shippingPhone', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.shippingPhonePlaceholder || '+966 50 000 0000'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.shippingAddress || 'عنوان الشحن'}
            </label>
            <textarea
              value={shippingAddress}
              onChange={(e) => onChange('shippingAddress', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.shippingAddressPlaceholder || 'أدخل عنوان الشحن الكامل'}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ClientInfoTab; 